import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, RefreshCw, Settings, Eye, CheckCircle2, Users, XCircle, Brain } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Loader from '../../components/ui/Loader';
import Badge from '../../components/ui/Badge';
import Card from '../../components/ui/Card';
import examenService from '../../services/examenService';
import perfilService from '../../services/perfilService';
import perfilPruebaService from '../../services/perfilPruebaService';
import preguntaService from '../../services/preguntaService';
import opcionService from '../../services/opcionService';
import RevisionExamen from './RevisionExamen';

export default function ExamenesRaven() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Detectar si estamos en admin o supervisor
  const isAdmin = location.pathname.startsWith('/admin');
  const baseRoute = isAdmin ? '/admin' : '/supervisor';
  
  const [loading, setLoading] = useState(true);
  const [examenes, setExamenes] = useState([]);
  const [perfiles, setPerfiles] = useState([]);
  const [asociaciones, setAsociaciones] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showAsignacionModal, setShowAsignacionModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [examenRevision, setExamenRevision] = useState(null);
  const [selectedExamen, setSelectedExamen] = useState(null);
  const [creandoExamenCompleto, setCreandoExamenCompleto] = useState(false);
  const [creandoPreguntas, setCreandoPreguntas] = useState(false);

  // Verificar si existe un examen Raven sin preguntas
  // Detecta por nombre que contenga "Raven" o por tipoExamen si existe
  const getExamenRavenSinPreguntas = () => {
    return examenes.find(e => {
      const esRaven = e.tipoExamen === 'RAVEN' || 
                      e.nombre?.toLowerCase().includes('raven');
      const sinPreguntas = !e.preguntas || e.preguntas.length === 0;
      return esRaven && sinPreguntas;
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar perfiles primero
      let perfilesData = [];
      try {
        const perfilesResponse = await perfilService.getAllPerfiles({ page: 0, size: 1000 }); // Cargar muchos perfiles para asignaciones
        console.log('Respuesta de perfiles:', perfilesResponse);
        
        // Manejar diferentes formatos de respuesta
        if (perfilesResponse && typeof perfilesResponse === 'object' && perfilesResponse.content) {
          // Respuesta paginada
          perfilesData = perfilesResponse.content;
        } else if (Array.isArray(perfilesResponse)) {
          // Respuesta como array directo
          perfilesData = perfilesResponse;
        } else {
          // Fallback
          perfilesData = [];
        }
        
        setPerfiles(perfilesData || []);
      } catch (perfilError) {
        console.error('Error cargando perfiles:', perfilError);
        toast.error('Error al cargar perfiles');
        setPerfiles([]);
      }

      // Cargar exámenes Raven
      let examenesData = [];
      try {
        examenesData = await examenService.getAllExamenes();
        setExamenes(examenesData || []);
      } catch (examenError) {
        console.error('Error cargando exámenes:', examenError);
        if (examenError.response?.status === 400) {
          console.log('La API de exámenes devolvió 400. Esto es normal si no hay exámenes creados.');
          setExamenes([]);
        } else {
          toast.error('Error al cargar exámenes');
          setExamenes([]);
        }
      }

      // Cargar asociaciones perfil-pruebas
      try {
        const asociacionesData = await perfilPruebaService.getAll();
        setAsociaciones(asociacionesData || []);
      } catch (error) {
        console.error('Error cargando asociaciones:', error);
        setAsociaciones([]);
      }
    } catch (error) {
      console.error('Error general:', error);
      setExamenes([]);
      setPerfiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingItem) {
        await examenService.updateExamen(editingItem.id, data);
        toast.success('Examen Raven actualizado');
      } else {
        // Crear el examen
        const nuevoExamen = await examenService.createExamen(data);
        console.log('✅ Examen Raven creado:', nuevoExamen);
        
        // Si se seleccionó un perfil, crear la asociación automáticamente
        const perfilId = data.perfiles?.[0]?.id;
        if (perfilId) {
          try {
            console.log(`🔗 Asociando examen ${nuevoExamen.id} al perfil ${perfilId}`);
            await perfilPruebaService.asociarExamenRaven(perfilId, nuevoExamen.id);
            toast.success('Examen Raven creado y asociado al perfil');
          } catch (asociacionError) {
            console.error('Error al asociar examen al perfil:', asociacionError);
            toast.warning('Examen creado pero no se pudo asociar al perfil automáticamente');
          }
        } else {
          toast.success('Examen Raven creado');
        }
      }
      loadData();
      closeModal();
    } catch (error) {
      toast.error('Error al guardar');
      console.error(error);
    }
  };

  // CREAR EXAMEN RAVEN COMPLETO AUTOMÁTICAMENTE (examen + preguntas + opciones + imágenes)
  const crearExamenRavenCompleto = async () => {
    // Verificar que exista el perfil 1
    const perfilExiste = Array.isArray(perfiles) ? perfiles.find(p => p.id === 1) : null;
    if (!perfilExiste) {
      toast.error('No existe el perfil con ID 1. Crea un perfil primero.');
      return;
    }

    if (!window.confirm('¿Crear Test Raven completo con 12 preguntas, 96 opciones e imágenes automáticamente?')) {
      return;
    }

    const toastId = toast.loading('Creando Test Raven completo...');
    setCreandoExamenCompleto(true);

    try {
      // 1. Crear el examen
      toast.loading('Paso 1/3: Creando examen...', { id: toastId });
      
      const examenData = {
        nombre: 'Test Raven',
        descripcion: 'Test de Matrices Progresivas de Raven - Evaluación de razonamiento abstracto y capacidad intelectual general',
        tiempoMinutos: 90,
        porcentajePonderacion: 100,
        estado: 'PENDIENTE',
        tipoExamen: 'RAVEN',
        perfiles: [{ id: 1 }]
      };

      const nuevoExamen = await examenService.createExamen(examenData);
      console.log('✅ Examen Raven creado:', nuevoExamen);

      // 2. Asociar al perfil 1
      toast.loading('Paso 2/3: Asociando al perfil...', { id: toastId });
      try {
        await perfilPruebaService.asociarExamenRaven(1, nuevoExamen.id);
        console.log('✅ Examen asociado al perfil 1');
      } catch (asocError) {
        console.warn('⚠️ Error asociando al perfil (puede que ya esté asociado):', asocError);
      }

      // 3. Crear preguntas y opciones
      await crearPreguntasYOpciones(nuevoExamen.id, toastId);

      toast.success('¡Test Raven creado exitosamente! 12 preguntas y 96 opciones con imágenes', { id: toastId });
      await loadData();

    } catch (error) {
      console.error('Error creando examen completo:', error);
      toast.error(`Error: ${error.response?.data?.message || error.message || 'Error desconocido'}`, { id: toastId });
    } finally {
      setCreandoExamenCompleto(false);
    }
  };

  // Función reutilizable para crear las 12 preguntas y 96 opciones
  const crearPreguntasYOpciones = async (examenId, toastId) => {
    // Respuestas correctas para cada pregunta
    const respuestasCorrectas = {
      1: 1, 2: 7, 3: 8, 4: 5, 5: 5, 6: 7,
      7: 6, 8: 8, 9: 1, 10: 1, 11: 6, 12: 3
    };

    for (let numPregunta = 1; numPregunta <= 12; numPregunta++) {
      toast.loading(`Creando pregunta ${numPregunta}/12...`, { id: toastId });

      // Intentar cargar la imagen de la pregunta
      let filePregunta = null;
      try {
        const responsePregunta = await fetch(`/assets/ravenpreguntasopciones/EXAMEN OFICIAL/${numPregunta}.PNG`);
        if (responsePregunta.ok) {
          const blobPregunta = await responsePregunta.blob();
          // Crear un File con el tipo MIME correcto
          filePregunta = new File([blobPregunta], `${numPregunta}.png`, { type: 'image/png' });
        }
      } catch (error) {
        console.warn(`No se pudo cargar imagen de pregunta ${numPregunta}:`, error);
      }

      // Crear FormData para la pregunta
      const formDataPregunta = new FormData();
      const preguntaObj = {
        texto: `Pregunta ${numPregunta}`,
        tipo: 'opcion_multiple',
        peso: 10,
        examenId: examenId,
        opciones: []
      };
      const preguntaBlob = new Blob([JSON.stringify(preguntaObj)], { type: 'application/json' });
      formDataPregunta.append('pregunta', preguntaBlob);
      
      // Agregar la imagen si existe (como File, no Blob)
      if (filePregunta) {
        formDataPregunta.append('imagen', filePregunta);
      }

      // Crear la pregunta
      const preguntaCreada = await preguntaService.createPreguntaWithImage(formDataPregunta);
      console.log(`✅ Pregunta ${numPregunta} creada con ID:`, preguntaCreada.id);

      // Crear 8 opciones para esta pregunta
      for (let numOpcion = 1; numOpcion <= 8; numOpcion++) {
        const esCorrecta = respuestasCorrectas[numPregunta] === numOpcion;

        toast.loading(`Pregunta ${numPregunta}/12 - Opción ${numOpcion}/8...`, { id: toastId });

        // Intentar cargar la imagen de la opción
        let fileOpcion = null;
        try {
          const responseOpcion = await fetch(`/assets/ravenpreguntasopciones/EXAMEN OFICIAL/${numPregunta}.${numOpcion}.PNG`);
          if (responseOpcion.ok) {
            const blobOpcion = await responseOpcion.blob();
            // Crear un File con el tipo MIME correcto
            fileOpcion = new File([blobOpcion], `${numPregunta}.${numOpcion}.png`, { type: 'image/png' });
          }
        } catch (error) {
          console.warn(`No se pudo cargar imagen de opción ${numPregunta}.${numOpcion}:`, error);
        }

        // Crear FormData para la opción
        const formDataOpcion = new FormData();
        const opcionObj = {
          texto: `Opción ${numOpcion}`,
          preguntaId: preguntaCreada.id,
          esCorrecta: esCorrecta
        };
        const opcionBlob = new Blob([JSON.stringify(opcionObj)], { type: 'application/json' });
        formDataOpcion.append('opcion', opcionBlob);
        
        // Agregar la imagen si existe (como File, no Blob)
        if (fileOpcion) {
          formDataOpcion.append('imagen', fileOpcion);
        }

        // Crear la opción
        await opcionService.createOpcionWithImage(formDataOpcion);
        console.log(`  ✅ Opción ${numPregunta}.${numOpcion} creada ${esCorrecta ? '(CORRECTA)' : ''}`);
      }
    }
  };

  // CREAR SOLO PREGUNTAS (cuando ya existe el examen pero sin preguntas)
  const crearSoloPreguntasRaven = async () => {
    const examenSinPreguntas = getExamenRavenSinPreguntas();
    if (!examenSinPreguntas) {
      toast.error('No se encontró un examen Raven sin preguntas');
      return;
    }

    if (!window.confirm(`¿Crear 12 preguntas y 96 opciones para el examen "${examenSinPreguntas.nombre}"?`)) {
      return;
    }

    const toastId = toast.loading('Creando preguntas y opciones...');
    setCreandoPreguntas(true);

    try {
      await crearPreguntasYOpciones(examenSinPreguntas.id, toastId);
      toast.success('¡Preguntas creadas exitosamente! 12 preguntas y 96 opciones con imágenes', { id: toastId });
      await loadData();
    } catch (error) {
      console.error('Error creando preguntas:', error);
      toast.error(`Error: ${error.response?.data?.message || error.message || 'Error desconocido'}`, { id: toastId });
    } finally {
      setCreandoPreguntas(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este examen?')) return;
    
    try {
      await examenService.deleteExamen(id);
      toast.success('Examen eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
      console.error(error);
    }
  };

  const openModal = (item = null) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const openAsignacionModal = (examen) => {
    setSelectedExamen(examen);
    setShowAsignacionModal(true);
  };

  const closeAsignacionModal = () => {
    setShowAsignacionModal(false);
    setSelectedExamen(null);
  };

  const handleTogglePerfil = async (perfilId, isAssigned) => {
    try {
      if (isAssigned) {
        // Encontrar y eliminar la asociación
        const asociacion = asociaciones.find(
          a => a.examenRavenId === selectedExamen.id && a.perfilId === perfilId
        );
        if (asociacion) {
          await perfilPruebaService.delete(asociacion.id);
          toast.success('Examen desasignado del perfil');
        }
      } else {
        // Crear nueva asociación
        await perfilPruebaService.asociarExamenRaven(perfilId, selectedExamen.id);
        toast.success('Examen asignado al perfil');
      }
      loadData();
    } catch (error) {
      console.error('Error al actualizar asociación:', error);
      toast.error('Error al actualizar asignación');
    }
  };

  const getPerfilesAsignados = (examenId) => {
    return asociaciones
      .filter(a => a.examenRavenId === examenId)
      .map(a => {
        const perfil = perfiles.find(p => p.id === a.perfilId);
        return perfil ? perfil.nombre : `Perfil #${a.perfilId}`;
      });
  };

  const getTodosLosPerfilesAsignados = () => {
    const perfilesUnicos = new Set();
    asociaciones
      .filter(a => a.examenRavenId != null)
      .forEach(a => {
        const perfil = Array.isArray(perfiles) ? perfiles.find(p => p.id === a.perfilId) : null;
        if (perfil) perfilesUnicos.add(perfil.nombre);
      });
    return Array.from(perfilesUnicos);
  };

  const filteredExamenes = examenes.filter(e =>
    e.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <Loader />;

  // Si hay un examen en revisión, mostrar el componente de revisión
  if (examenRevision) {
    return <RevisionExamen examenId={examenRevision} onVolver={() => setExamenRevision(null)} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-neutral-800">Exámenes Raven</h1>
              <p className="text-neutral-600 mt-1">Gestión de exámenes de matrices progresivas</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {/* Si NO hay exámenes Raven, mostrar botón de crear completo */}
          {examenes.length === 0 && (
            <Button 
              onClick={crearExamenRavenCompleto} 
              disabled={creandoExamenCompleto}
              className="gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              <Brain className="w-4 h-4" />
              {creandoExamenCompleto ? 'Creando...' : 'Crear Test Raven Completo'}
            </Button>
          )}
          
          {/* Si existe examen pero SIN preguntas, mostrar botón de crear solo preguntas */}
          {getExamenRavenSinPreguntas() && (
            <Button 
              onClick={crearSoloPreguntasRaven} 
              disabled={creandoPreguntas}
              className="gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
            >
              <Plus className="w-4 h-4" />
              {creandoPreguntas ? 'Creando preguntas...' : 'Crear Preguntas Automáticas'}
            </Button>
          )}
          
          {/* Botón para asignar perfiles (siempre visible si hay exámenes) */}
          {examenes.length > 0 && (
            <Button 
              onClick={() => examenes.length > 0 && openAsignacionModal(examenes[0])} 
              className="gap-2"
              variant="secondary"
            >
              <Users className="w-4 h-4" />
              Asignar Perfiles
            </Button>
          )}
        </div>
      </div>

      {/* Card de perfiles con exámenes Raven */}
      <Card className="border-l-4 border-l-blue-500">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-neutral-800 mb-2">Perfiles con Exámenes Raven Habilitados</h3>
              {getTodosLosPerfilesAsignados().length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {getTodosLosPerfilesAsignados().map((nombre, idx) => (
                    <Badge key={idx} variant="primary" className="bg-gradient-to-r from-white-600 to-indigo-600">
                      {nombre}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-500 italic">
                  No hay perfiles con Exámenes Raven habilitados. Usa el botón "Asignar a Perfiles" (👥) en cada examen.
                </p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Tarjeta Informativa del Test Raven */}
      <Card className="overflow-hidden hover:shadow-lg transition-shadow">
        <div className="flex flex-col md:flex-row">
          {/* Imagen */}
          <div className="md:w-1/3 h-48 md:h-auto bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center relative overflow-hidden">
            <img 
              src="/assets/raven-test.jpg" 
              alt="Test Raven" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center" style={{display: 'none'}}>
              <Brain className="w-24 h-24 text-white/80" />
            </div>
            <div className="absolute top-3 right-3">
              <Badge className="bg-white/20 text-white border-white/30">Cognitivo</Badge>
            </div>
          </div>
          
          {/* Contenido */}
          <div className="md:w-2/3 p-6">
            <h3 className="text-2xl font-bold text-neutral-800 mb-3 flex items-center gap-2">
              <Brain className="w-6 h-6 text-blue-600" />
              Test de Matrices Progresivas de Raven
            </h3>
            <p className="text-neutral-600 mb-4">
              Prueba psicométrica diseñada para medir la capacidad de razonamiento abstracto y la inteligencia general no verbal. 
              Considerado uno de los tests más confiables para evaluar el factor "g" de inteligencia.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">¿Qué evalúa?</h4>
                <ul className="space-y-1 text-sm text-neutral-600">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                    Inteligencia fluida
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                    Razonamiento lógico-abstracto
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                    Capacidad de aprendizaje
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                    Resolución de problemas
                  </li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold text-neutral-700 text-sm uppercase tracking-wide">Características</h4>
                <ul className="space-y-1 text-sm text-neutral-600">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                    12 matrices progresivas
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                    8 opciones por pregunta
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                    Duración: 45-90 minutos
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                    Sin sesgo cultural/idiomático
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3 pt-4 border-t border-neutral-100">
              <div className="flex items-center gap-2 text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full">
                <span>📊</span> Percentil + Clasificación
              </div>
              <div className="flex items-center gap-2 text-xs bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-full">
                <span>🎯</span> Alta validez predictiva
              </div>
              <div className="flex items-center gap-2 text-xs bg-purple-50 text-purple-700 px-3 py-1.5 rounded-full">
                <span>🌍</span> Aplicable internacionalmente
              </div>
            </div>
          </div>
        </div>
      </Card>
{/*
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-4">
        <div className="flex-1 sm:max-w-md">
          <Input
            placeholder="Buscar exámenes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
        </div>
        <Button variant="outline" onClick={loadData} className="gap-2 w-full sm:w-auto justify-center">
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </Button>
      </div>

       Vista Desktop - Tabla 
      <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Nombre</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Descripción</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Perfiles Asignados</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Estado</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Tiempo (min)</th>
              <th className="text-right p-4 text-sm font-semibold text-neutral-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {filteredExamenes.length === 0 ? (
              <tr>
                <td colSpan="6" className="p-8 text-center text-neutral-500">
                  No se encontraron exámenes Raven
                </td>
              </tr>
            ) : (
              filteredExamenes.map((examen) => {
                const perfilesAsignados = getPerfilesAsignados(examen.id);
                
                const getEstadoBadge = (estado) => {
                  const estados = {
                    'PENDIENTE': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', text: 'Pendiente' },
                    'PROGRESO': { color: 'bg-blue-100 text-blue-800 border-blue-200', text: 'En Progreso' },
                    'FINALIZADO': { color: 'bg-green-100 text-green-800 border-green-200', text: 'Finalizado' }
                  };
                  const config = estados[estado] || { color: 'bg-gray-100 text-gray-800 border-gray-200', text: estado || 'N/A' };
                  return (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
                      {config.text}
                    </span>
                  );
                };

                return (
                  <tr key={examen.id} className="hover:bg-neutral-50">
                    <td className="p-4 font-medium text-neutral-800">{examen.nombre}</td>
                    <td className="p-4 text-neutral-600">{examen.descripcion}</td>
                    <td className="p-4">
                      {perfilesAsignados.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {perfilesAsignados.map((nombre, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {nombre}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-neutral-400 italic">Sin asignar</span>
                      )}
                    </td>
                    <td className="p-4">{getEstadoBadge(examen.estado)}</td>
                    <td className="p-4 text-neutral-600">{examen.tiempoMinutos || 'N/A'}</td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openAsignacionModal(examen)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                          title="Asignar a perfiles"
                        >
                          <Users className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setExamenRevision(examen.id)}
                          className="p-2 text-info hover:bg-info-50 rounded-md transition-colors"
                          title="Revisar respuestas"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            const ruta = `${baseRoute}/examenes/gestionar/${examen.id}`;
                            console.log('🔍 Gestionar Examen:', { examenId: examen.id, ruta, isAdmin });
                            navigate(ruta);
                          }}
                          className="p-2 text-secondary hover:bg-secondary-50 rounded-md transition-colors"
                          title="Gestionar preguntas y opciones"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openModal(examen)}
                          className="p-2 text-primary hover:bg-primary-50 rounded-md transition-colors"
                          title="Editar examen"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(examen.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Eliminar examen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Vista Mobile/Tablet - Cards 
      <div className="lg:hidden space-y-3">
        {filteredExamenes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8 text-center text-neutral-500">
            No se encontraron exámenes Raven
          </div>
        ) : (
          filteredExamenes.map((examen) => {
            const perfilesAsignados = getPerfilesAsignados(examen.id);
            
            const getEstadoBadge = (estado) => {
              const estados = {
                'PENDIENTE': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', text: 'Pendiente' },
                'PROGRESO': { color: 'bg-blue-100 text-blue-800 border-blue-200', text: 'En Progreso' },
                'FINALIZADO': { color: 'bg-green-100 text-green-800 border-green-200', text: 'Finalizado' }
              };
              const config = estados[estado] || { color: 'bg-gray-100 text-gray-800 border-gray-200', text: estado || 'N/A' };
              return (
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
                  {config.text}
                </span>
              );
            };

            return (
              <div key={examen.id} className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-neutral-800 text-lg">{examen.nombre}</h3>
                  {getEstadoBadge(examen.estado)}
                </div>
                
                <p className="text-sm text-neutral-600 mb-3">{examen.descripcion || 'Sin descripción'}</p>
                
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Tiempo:</span>
                    <span className="font-medium text-neutral-700">{examen.tiempoMinutos || 'N/A'} min</span>
                  </div>
                  <div>
                    <span className="text-neutral-500 block mb-1">Perfiles:</span>
                    {perfilesAsignados.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {perfilesAsignados.map((nombre, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {nombre}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-neutral-400 italic">Sin asignar</span>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 pt-3 border-t border-neutral-100">
                  <button
                    onClick={() => openAsignacionModal(examen)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-green-600 bg-green-50 hover:bg-green-100 rounded-md transition-colors"
                  >
                    <Users className="w-4 h-4" />
                    <span className="hidden sm:inline">Asignar</span>
                  </button>
                  <button
                    onClick={() => setExamenRevision(examen.id)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-info bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="hidden sm:inline">Revisar</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      const ruta = `${baseRoute}/examenes/gestionar/${examen.id}`;
                      navigate(ruta);
                    }}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-secondary bg-orange-50 hover:bg-orange-100 rounded-md transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    <span className="hidden sm:inline">Gestionar</span>
                  </button>
                  <button
                    onClick={() => openModal(examen)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-primary bg-teal-50 hover:bg-teal-100 rounded-md transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Editar</span>
                  </button>
                  <button
                    onClick={() => handleDelete(examen.id)}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Eliminar</span>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
*/}
      <ExamenModal
        isOpen={showModal}
        onClose={closeModal}
        onSave={handleSave}
        initialData={editingItem}
        perfiles={perfiles}
      />

      <AsignacionPerfilesModal
        isOpen={showAsignacionModal}
        onClose={closeAsignacionModal}
        examen={selectedExamen}
        perfiles={perfiles}
        asociaciones={asociaciones}
        onTogglePerfil={handleTogglePerfil}
      />
    </div>
  );
}

function ExamenModal({ isOpen, onClose, onSave, initialData, perfiles }) {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    tiempoMinutos: '',
    porcentajePonderacion: '',
    estado: 'PENDIENTE',
    tipoExamen: 'RAVEN',
    perfiles: []
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        nombre: initialData.nombre || '',
        descripcion: initialData.descripcion || '',
        tiempoMinutos: initialData.tiempoMinutos || '',
        porcentajePonderacion: initialData.porcentajePonderacion || '',
        estado: initialData.estado || 'PENDIENTE',
        tipoExamen: 'RAVEN',
        perfiles: initialData.perfilId ? [{ id: initialData.perfilId }] : 
                  initialData.perfiles || 
                  (initialData.perfil?.id ? [{ id: initialData.perfil.id }] : [])
      });
    } else {
      setFormData({
        nombre: '',
        descripcion: '',
        tiempoMinutos: '',
        porcentajePonderacion: '100',
        estado: 'PENDIENTE',
        tipoExamen: 'RAVEN',
        perfiles: []
      });
    }
  }, [initialData, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      tiempoMinutos: parseInt(formData.tiempoMinutos),
      porcentajePonderacion: 100, // Siempre 100%
      estado: initialData ? formData.estado : 'PENDIENTE'
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Editar Examen Raven' : 'Nuevo Examen Raven'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="p-3 rounded-md text-sm bg-blue-50 text-blue-800 border border-blue-200">
          <div className="flex items-start gap-2">
            <span className="text-lg">🧩</span>
            <div>
              <strong>Exámenes Raven:</strong> Evaluación de razonamiento abstracto mediante matrices progresivas.
            </div>
          </div>
        </div>

        <Input
          label="Nombre"
          value={formData.nombre}
          onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
          required
          placeholder="Ej: Test Raven Avanzado"
        />
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Descripción
          </label>
          <textarea
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            rows={2}
            value={formData.descripcion}
            onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
            required
            placeholder="Evaluación de razonamiento abstracto mediante matrices progresivas"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Perfil
          </label>
          <select
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            value={formData.perfiles?.[0]?.id || ''}
            onChange={(e) => setFormData({ ...formData, perfiles: e.target.value ? [{ id: parseInt(e.target.value) }] : [] })}
            required
          >
            <option value="">Seleccionar perfil</option>
            {Array.isArray(perfiles) ? perfiles.map(p => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            )) : null}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Tiempo (minutos)"
            type="number"
            value={formData.tiempoMinutos}
            onChange={(e) => setFormData({ ...formData, tiempoMinutos: e.target.value })}
            required
            placeholder="45"
          />
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Ponderación (%)
            </label>
            <input
              type="number"
              value="100"
              disabled
              className="w-full px-3 py-2 border border-neutral-300 rounded-md bg-neutral-100 text-neutral-600 cursor-not-allowed"
            />
            <p className="text-xs text-neutral-500 mt-1">Valor fijo: 100%</p>
          </div>
        </div>

        <div className="p-3 rounded-md text-sm bg-blue-50 text-blue-800 border border-blue-200">
          <strong>📝 Siguiente paso:</strong> Después de crear el examen:
          <ul className="mt-2 ml-4 list-disc space-y-1">
            <li>El examen se asociará automáticamente al perfil seleccionado</li>
            <li>Puedes usar el botón <strong>"Asignar a Perfiles"</strong> (👥) para agregar más perfiles</li>
            <li>Usa el botón <strong>"Gestionar"</strong> (⚙️) para agregar las preguntas y opciones del Test Raven</li>
          </ul>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit">
            Guardar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function AsignacionPerfilesModal({ isOpen, onClose, examen, perfiles, asociaciones, onTogglePerfil }) {
  if (!examen) return null;

  const isPerfilAsignado = (perfilId) => {
    return asociaciones.some(
      a => a.examenRavenId === examen.id && a.perfilId === perfilId
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Asignar "${examen.nombre}" a Perfiles`}
      size="lg"
    >
      <div className="space-y-4">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Tip:</strong> Selecciona los perfiles que podrán acceder a este examen Raven. 
            Los usuarios asignados a estos perfiles verán el examen disponible en su panel.
          </p>
        </div>

        <div className="space-y-2">
          {(!Array.isArray(perfiles) || perfiles.length === 0) ? (
            <p className="text-center text-neutral-500 py-8">No hay perfiles disponibles</p>
          ) : (
            perfiles.map((perfil) => {
              const isAsignado = isPerfilAsignado(perfil.id);
              
              return (
                <div
                  key={perfil.id}
                  onClick={() => onTogglePerfil(perfil.id, isAsignado)}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    isAsignado
                      ? 'border-green-500 bg-green-50 hover:bg-green-100'
                      : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                  }`}
                >
                  <div className="flex-1">
                    <h3 className={`font-semibold ${isAsignado ? 'text-green-800' : 'text-neutral-800'}`}>
                      {perfil.nombre}
                    </h3>
                    <p className={`text-sm ${isAsignado ? 'text-green-600' : 'text-neutral-600'}`}>
                      {perfil.descripcion || 'Sin descripción'}
                    </p>
                  </div>
                  <div className="ml-4">
                    {isAsignado ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : (
                      <XCircle className="w-6 h-6 text-neutral-400" />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onClose} variant="primary">
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
